import React, { useCallback, useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import NiceModal from "@ebay/nice-modal-react";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { getHomepage } from "utils";
import { useQuery } from "urql";
import { ProjectCreateMut } from "api/mutations";
import { useTranslate } from "hooks";
import { NUMBER_OF_PROJECTS_FETCHED } from "variables/projects";
import { useProjectActions } from "stores/project/project/projectStore";
import { ProjectListTitlesAndIds } from "../../api/queries";
import { ModalWrapper } from "../ModalWrapper";
import { Button } from "../Button";
import { Select } from "../Select/Select";
import { ProjectDetailsModal } from "./ProjectDetailsModal/ProjectDetailsModal";
import type { SelectOption } from "interfaces/shared.types";

type Props = {
  wizardItem?: Scrivito.Obj;
  setActiveButton?: (value: null) => void;
};

type CreatedProject = {
  id: string;
  title: string;
};

export const AddWizardToProject = NiceModal.create(
  Scrivito.connect(
    ({ wizardItem, setActiveButton = () => {} }: Props): React.ReactElement => {
      const translate = useTranslate();
      const modal = NiceModal.useModal();
      const { set: setProject } = useProjectActions();

      const [selectedProject, setSelectedProject] =
        useState<SelectOption | null>(null);

      const handleCloseModal = useCallback(() => {
        modal.resolve();
        modal.remove();
      }, [modal]);

      const [{ data, fetching }] = useQuery({
        query: ProjectListTitlesAndIds,
        variables: {
          limit: NUMBER_OF_PROJECTS_FETCHED,
          page: 1,
        },
        requestPolicy: "network-only",
      });

      const { items: projects } = data?.currentUser?.projectList ?? {};

      const options = useMemo(
        (): SelectOption[] | undefined =>
          projects
            ?.filter((item): item is NonNullable<typeof item> => item !== null)
            .map((item) => ({ value: item.id, label: item.title ?? item.id })),
        [projects],
      );

      const configWizardObjRefs = getHomepage()?.get("configWizardObjRefs") as
        | Scrivito.Obj[]
        | undefined;

      const wizardObjRef = configWizardObjRefs?.find(
        (item) => item.objClass() === "Wizard",
      );

      const subType = wizardItem?.get("subType") as string | undefined;

      const saveHandler = useCallback(
        (selectedProjectOption: SelectOption) => {
          setProject(selectedProjectOption.value);

          Scrivito.navigateTo(
            () => wizardObjRef ?? (Scrivito.currentPage() as Scrivito.Obj),
            {
              params: {
                subType: subType ?? null,
                projectId: selectedProjectOption.value,
                step: "0",
              },
            },
          );
          handleCloseModal();
          setActiveButton(null);
        },
        [setProject, subType, handleCloseModal, setActiveButton, wizardObjRef],
      );

      const toggleProjectDetailsModal = useCallback(() => {
        NiceModal.show(ProjectDetailsModal, {
          mutation: ProjectCreateMut,
        }).then((result) => {
          const createdProjectData = result as CreatedProject;
          saveHandler({
            value: createdProjectData.id,
            label: createdProjectData.title,
          });
          handleCloseModal();
        });
      }, [saveHandler, handleCloseModal]);

      return (
        <ModalWrapper
          isOpen={modal.visible}
          centered={true}
          size="md"
          toggle={modal.hide}
          isLoading={options === undefined || fetching}
        >
          <ModalHeader tag="div" className="d-flex align-items-start pb-0">
            <h3 className="text-uppercase mb-1">
              {translate("ADD_TO_PROJECT")}
            </h3>
            <h4 className="mb-2_5">{translate("PLEASE_CHOOSE_PROJECT")}</h4>
            <div>{translate("PLEASE_CHOOSE_PROJECT_TEXT")}</div>
          </ModalHeader>
          <ModalBody className="m-0 pt-2_5 d-flex row pb-0">
            <h5 className="mb-2 p-0">{translate("EXISTING_PROJECTS")}</h5>
            <Select
              name="config-"
              data-recording-disable={true}
              options={options}
              onChange={(e) => setSelectedProject(e)}
              wrapperClassNames="p-0"
              testId="add-wizard-to-project-select"
            />
            <Button
              data-testid="AddWizardToProject_create-new-project-btn"
              color="secondary"
              outline
              onClick={toggleProjectDetailsModal}
              classNames="btn-sm w-auto m-3 ms-0"
            >
              {translate("CREATE_A_NEW_PROJECT")}
            </Button>
          </ModalBody>
          <ModalFooter className="pt-0">
            <div className="m-0 d-flex">
              <Button
                data-testid="AddWizardToProject_create-save-btn"
                color="primary"
                onClick={() => selectedProject && saveHandler(selectedProject)}
                classNames="m-1"
                disabled={!selectedProject?.value}
              >
                {translate("SAVE")}
              </Button>
            </div>
          </ModalFooter>
        </ModalWrapper>
      );
    },
  ),
);
